package com.daytimegaming.ourdailyfinances.presentation.navigation

import androidx.compose.runtime.Composable
import androidx.navigation3.runtime.NavBackStack
import androidx.navigation3.runtime.NavKey

expect fun getInitialWebRoute(): AppRoute?

expect fun triggerBrowserBack(count: Int): Boolean

@Composable
expect fun BrowserNavigationSync(backStack: NavBackStack<NavKey>, isAuthenticated: Boolean)

fun routeToPath(route: AppRoute): String {
    return when (route) {
        is AppRoute.AddRealEstateHolding -> "/real-estate/add"
        is AppRoute.ViewRealEstateHolding -> "/real-estate/view/${route.realEstateHoldingId}"
        is AppRoute.UpdateRealEstateHolding -> "/real-estate/update/${route.realEstateHoldingId}"
        is AppRoute.AccountDetail -> {
            if (route.dashboardId != null) {
                "/account/${route.accountId}/dashboard/${route.dashboardId}"
            } else {
                "/account/${route.accountId}"
            }
        }
        is AppRoute.SecurityHoldingDetail -> {
            if (route.dashboardId != null) {
                "/account/${route.accountId}/security/${route.securityId}/dashboard/${route.dashboardId}"
            } else {
                "/account/${route.accountId}/security/${route.securityId}"
            }
        }
        is AppRoute.CategorySpending -> "/spending/category/${route.categoryName}/range/${route.range}"
        is AppRoute.ConfigureDashboard -> "/dashboard/configure/${route.dashboardId}"
        is AppRoute.DashboardDetail -> "/dashboard/${route.dashboardId}"
        is AppRoute.DashboardTransactions -> "/dashboard/transactions/${route.dashboardId}"
        is AppRoute.RecurringTransactionDetail -> "/recurring/detail/${route.streamId}"
        is AppRoute.TransactionDetail -> {
            if (route.dashboardId != null) {
                "/transaction/${route.transactionId}/dashboard/${route.dashboardId}"
            } else {
                "/transaction/${route.transactionId}"
            }
        }
        is AppRoute.UpdateBudget -> "/budget/update/${route.budgetId}"
        is AppRoute.UpdateUserInfo -> "/user/update-info?email=${route.email}"
        is AppRoute.ViewBudget -> "/budget/view/${route.budgetId}"
        is AppRoute.CreateBudget -> "/budget/create"
        is AppRoute.CreateDashboard -> "/dashboard/create"
        is AppRoute.Home -> "/home/${route.tab.lowercase()}"
        is AppRoute.JoinDashboard -> "/dashboard/join"
        is AppRoute.Login -> "/login"
        is AppRoute.ManageAccounts -> "/manage-accounts"
        is AppRoute.RecentSpending -> "/spending/recent"
        is AppRoute.RecurringTransactions -> "/spending/recurring"
        is AppRoute.IncomeVsExpensesSummary -> "/income-vs-expenses"
        is AppRoute.MonthlyBreakdown -> "/income-vs-expenses/month/${route.yearMonth.replace(" ", "%20")}"
        is AppRoute.Register -> "/register"
        is AppRoute.Splash -> "/splash"
        is AppRoute.Landing -> "/"
        is AppRoute.ManageSubscription -> "/manage-subscription"
        is AppRoute.PrivacyPolicy -> "/privacy-policy"
        is AppRoute.TermsOfService -> "/terms-of-service"
    }
}

fun pathToRoute(path: String): AppRoute? {
    val cleanPath = path.substringBefore("?").removePrefix("#").removePrefix("/").trim()
    val segments = cleanPath.split("/").filter { it.isNotEmpty() }
    
    val queryParams = mutableMapOf<String, String>()
    if (path.contains("?")) {
        val queryString = path.substringAfter("?")
        queryString.split("&").forEach { param ->
            val parts = param.split("=")
            if (parts.size == 2) {
                queryParams[parts[0]] = parts[1]
            }
        }
    }

    if (segments.isEmpty()) return AppRoute.Landing

    return when (segments[0]) {
        "real-estate" -> {
            if (segments.size > 1) {
                when (segments[1]) {
                    "add" -> AppRoute.AddRealEstateHolding
                    "view" -> if (segments.size > 2) AppRoute.ViewRealEstateHolding(segments[2]) else null
                    "update" -> if (segments.size > 2) AppRoute.UpdateRealEstateHolding(segments[2]) else null
                    else -> null
                }
            } else null
        }
        "account" -> {
            if (segments.size > 3 && segments[2] == "security") {
                val accountId = segments[1]
                val securityId = segments[3]
                val dashboardId = if (segments.size > 5 && segments[4] == "dashboard") segments[5] else null
                AppRoute.SecurityHoldingDetail(accountId, securityId, dashboardId)
            } else if (segments.size > 1) {
                val accountId = segments[1]
                val dashboardId = if (segments.size > 3 && segments[2] == "dashboard") segments[3] else null
                AppRoute.AccountDetail(accountId, dashboardId)
            } else null
        }
        "spending" -> {
            if (segments.size > 1) {
                when (segments[1]) {
                    "category" -> {
                        if (segments.size > 4 && segments[3] == "range") {
                            AppRoute.CategorySpending(segments[2], segments[4])
                        } else null
                    }
                    "recent" -> AppRoute.RecentSpending
                    "recurring" -> AppRoute.RecurringTransactions
                    else -> null
                }
            } else null
        }
        "dashboard" -> {
            if (segments.size > 1) {
                when (segments[1]) {
                    "configure" -> if (segments.size > 2) AppRoute.ConfigureDashboard(segments[2]) else null
                    "transactions" -> if (segments.size > 2) AppRoute.DashboardTransactions(segments[2]) else null
                    "create" -> AppRoute.CreateDashboard
                    "join" -> AppRoute.JoinDashboard
                    else -> AppRoute.DashboardDetail(segments[1])
                }
            } else null
        }
        "recurring" -> {
            if (segments.size > 2 && segments[1] == "detail") {
                AppRoute.RecurringTransactionDetail(segments[2])
            } else null
        }
        "transaction" -> {
            if (segments.size > 1) {
                val transactionId = segments[1]
                val dashboardId = if (segments.size > 3 && segments[2] == "dashboard") segments[3] else null
                AppRoute.TransactionDetail(transactionId, dashboardId)
            } else null
        }
        "budget" -> {
            if (segments.size > 1) {
                when (segments[1]) {
                    "update" -> if (segments.size > 2) AppRoute.UpdateBudget(segments[2]) else null
                    "view" -> if (segments.size > 2) AppRoute.ViewBudget(segments[2]) else null
                    "create" -> AppRoute.CreateBudget
                    else -> null
                }
            } else null
        }
        "user" -> {
            if (segments.size > 1 && segments[1] == "update-info") {
                val email = queryParams["email"] ?: ""
                AppRoute.UpdateUserInfo(email)
            } else null
        }
        "home" -> {
            val tabName = if (segments.size > 1) segments[1].lowercase() else "dashboards"
            val validTab = when (tabName) {
                "dashboards", "accounts", "transactions", "settings" -> tabName
                else -> "dashboards"
            }
            AppRoute.Home(validTab)
        }
        "login" -> AppRoute.Login
        "manage-accounts" -> AppRoute.ManageAccounts
        "manage-subscription" -> AppRoute.ManageSubscription
        "income-vs-expenses" -> {
            if (segments.size > 2 && segments[1] == "month") {
                val decodedMonth = segments[2].replace("%20", " ").replace("+", " ")
                AppRoute.MonthlyBreakdown(decodedMonth)
            } else {
                AppRoute.IncomeVsExpensesSummary
            }
        }
        "register" -> AppRoute.Register
        "privacy-policy", "privacy" -> AppRoute.PrivacyPolicy
        "terms-of-service", "terms" -> AppRoute.TermsOfService
        "splash" -> AppRoute.Landing
        "landing" -> AppRoute.Landing
        else -> null
    }
}
